import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login: authLogin, check2FA, user } = useAuth();

    // Redirect to dashboard if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!attemptId) return;

    let cancelled = false;
    const interval = setInterval(async () => {
      if (cancelled) return;
      const result = await check2FA(attemptId);
      if (cancelled) return;
      if (result.success) {
        setAttemptId(null);
        clearInterval(interval);
        // Полный reload вместо navigate(): cookie access_token уже set HttpOnly,
        // AuthContext на /dashboard сам подхватит юзера через /api/auth/current.
        // Это решает race-condition «setUser ещё не успел сработать → protected route
        // редиректит обратно на /login» (см. фикс #192).
        window.location.replace('/dashboard');
      } else if (!result.pending) {
        setAttemptId(null);
        clearInterval(interval);
        setError('Вход отклонен или истек срок действия.');
      }
    }, 2000);

    return () => { cancelled = true; clearInterval(interval); };
    }, [attemptId, check2FA]);

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authLogin(login, password);
      if (result.requires2fa && result.attemptId) {
        setAttemptId(result.attemptId);
      } else if (result.success) {
        // Полный reload — как и в 2FA-flow, надёжнее navigate()
        window.location.replace('/dashboard');
      } else {
        setError('Неверный логин или пароль');
      }
    } catch (err) {
      setError('Ошибка при входе. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  if (attemptId) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <i className="bi bi-shield-lock" style={{ fontSize: '3rem', color: '#6366f1' }}></i>
            <h3>Проверка 2FA</h3>
            <p>Подтвердите вход в <b>Telegram</b></p>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Ожидание...</span>
            </div>
            <p style={{ marginTop: '15px', color: '#64748b' }}>Ожидаем подтверждения...</p>
          </div>
          <button onClick={() => setAttemptId(null)} className="login-button" style={{ marginTop: '30px', background: '#e2e8f0', color: '#475569' }}>
            Отмена
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <i className="bi bi-headset"></i>
          <h3>Система поддержки</h3>
          <p>Войдите для продолжения работы</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="login">Логин или Email</label>
            <div className="input-group">
              <span className="input-icon"><i className="bi bi-person"></i></span>
              <input
                type="text"
                id="login"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                required
                disabled={loading}
                autoFocus
                placeholder="Введите логин или email"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="password">Пароль</label>
            <div className="input-group">
              <span className="input-icon"><i className="bi bi-lock"></i></span>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Введите пароль"
              />
            </div>
          </div>

          <div className="remember-row">
            <label className="remember-check">
              <input type="checkbox" disabled={loading} />
              <span>Запомнить меня</span>
            </label>
          </div>

          <button type="submit" disabled={loading} className="login-button">
            <i className="bi bi-box-arrow-in-right"></i>
            {loading ? 'Вход...' : 'Войти'}
          </button>
        </form>

        <div className="login-links">
          <a href="/forgot-password">Забыли пароль?</a>
          <a href="/register-invite">Регистрация по инвайту</a>
        </div>

        <hr className="login-divider" />

        <div className="login-secure">
          <i className="bi bi-shield-check"></i>
          Защищенное соединение
        </div>
      </div>
    </div>
  );
};

export default Login;
